import type { IVariants } from 'components/TimeScaleSelect/types';
import type { ChartFilterType } from 'customTypes/filterTypes';
import formatDateLabel from 'utils/chart/formatDateLabel';
import formatNumber from 'utils/formatNumber';
import type { IChartModeColors, IChartTrend, IMainChartData, ITrendColorsType } from '../types';

export type chartPeriodType = '1h' | '1d' | '7d' | '30d' | '365d';

export type cmcPeriodType = 'hourly' | 'daily';
export type cmcIntervalType = '1h' | '4h' | '1d' | 'daily';
export interface ICmcTimeSettings {
	timePeriod: cmcPeriodType;
	interval: cmcIntervalType;
}

export const cmcIntervals: Record<chartPeriodType, ICmcTimeSettings> = {
	'1h': {
		timePeriod: 'hourly',
		interval: '1h',
	},
	'1d': {
		timePeriod: 'hourly',
		interval: '1h',
	},
	'7d': {
		timePeriod: 'hourly',
		interval: '4h',
	},
	'30d': {
		timePeriod: 'daily',
		interval: 'daily',
	},
	'365d': {
		timePeriod: 'daily',
		interval: 'daily',
	},
};

export const variants: IVariants[] = [
	{ value: '1h', name: '1 hour', scale: '1H' },
	{ value: '1d', name: '1 day', scale: '24H' },
	{ value: '7d', name: '1 week', scale: '1W' },
	{ value: '30d', name: '1 month', scale: '1M' },
	{ value: '365d', name: '1 year', scale: '1Y' },
];
export const scale = {
	year: '1Y',
	month: '1M',
	week: '1W',
	day: '24H',
	hour: '1H',
};

export const coinrankingOptions = {
	'1h': {
		interval: 'minute',
		candleLimit: 60,
	},
	'1d': {
		interval: 'hour',
		candleLimit: 24,
	},
	'7d': {
		interval: 'hour',
		candleLimit: 168,
	},
	'30d': {
		interval: '8hours',
		candleLimit: 90,
	},
	'365d': {
		interval: 'day',
		candleLimit: 365,
	},
};

export const trendColors: ITrendColorsType = {
	upTrend: {
		accent: '#ED6C4D',
		blurred: '#ed6c4d4d',
	},
	downTrend: {
		accent: '#4B8185',
		blurred: '#4b818533',
	},
	stable: {
		accent: '#E3A400',
		blurred: '#ffb90433',
	},
};

export const chartModeColors: IChartModeColors = {
	dark: {
		text: '#FFFFFF',
		chartGrid: '#ffffff33',
		tooltip: {
			backgroundColor: '#0E1A1B',
			titleColor: '#FFFFFF',
			bodyColor: '#FFFFFF',
		},
	},
	light: {
		text: '#00000080',
		chartGrid: '#0000001a',
		tooltip: {
			backgroundColor: '#FFFFFF',
			titleColor: '#0E1A1B',
			bodyColor: '#00000080',
		},
	},
};

export function getTrendDirectionColor(data: IMainChartData, type: ChartFilterType): IChartTrend {
	const start = data[type][0];
	const end = data[type][data?.price.length - 1];
	const result = start - end;

	if (result > 0) {
		return trendColors.upTrend;
	}
	if (result < 0) {
		return trendColors.downTrend;
	}
	if (result === 0) {
		return trendColors.stable;
	}
	return trendColors.stable;
}

export function createChartConfig(
	theme: 'dark' | 'light',
	chartData: IMainChartData,
	chartFilterType: ChartFilterType,
	currentPeriod: chartPeriodType,
	quoteCurrency: string,
) {
	let trend = getTrendDirectionColor(chartData, chartFilterType);
	let data = {
		labels: chartData.labels,
		datasets: [
			{
				data: chartData[chartFilterType],
				fill: true,
				borderColor: trend.accent,
				backgroundColor: trend.blurred,
			},
		],
	};
	let config = {
		type: 'line',
		data: data,
		options: {
			animation: {
				duration: 0,
			},
			maintainAspectRatio: false,
			elements: {
				line: {
					borderWidth: 2,
				},
				point: {
					pointBackgroundColor: 'transparent',
					radius: 0,
					hoverRadius: 5,
					hoverBorderWidth: 2,
					pointHoverBackgroundColor: trend.accent,
					pointHoverBorderColor: '#FFFFFF',
				},
			},
			scales: {
				y: {
					display: true,
					grid: {
						borderColor: chartModeColors[theme].chartGrid,
						color: chartModeColors[theme].chartGrid,
						drawTicks: false,
					},
					ticks: {
						color: chartModeColors[theme].text,
						callback: (label) => label.toLocaleString(),
						font: {
							size: 10,
							family: 'Poppins',
						},
						padding: 10,
						backdropPadding: 10,
					},
				},
				x: {
					display: true,
					grid: {
						borderDash: [2],
						color: chartModeColors[theme].chartGrid,
						drawTicks: false,
					},
					ticks: {
						maxRotation: 0,
						minRotation: 0,
						autoSkip: true,
						maxTicksLimit: 10,
						color: chartModeColors[theme].text,
						callback: (_, index: number) => {
							return formatDateLabel(currentPeriod, new Date(chartData.labels[index]));
						},
						font: {
							size: 10,
							family: 'Poppins',
						},
						padding: 10,
					},
				},
			},
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					padding: {
						top: 8,
						bottom: 8,
						left: 16,
						right: 16,
					},
					titleFont: {
						size: 12,
					},
					bodyFont: {
						size: 10,
					},
					backgroundColor: chartModeColors[theme].tooltip.backgroundColor,
					titleColor: chartModeColors[theme].tooltip.titleColor,
					titleSpacing: 100,
					bodyColor: chartModeColors[theme].tooltip.bodyColor,
					displayColors: false,
					mode: 'nearest',
					intersect: false,
					callbacks: {
						title: function (context) {
							return formatNumber(
								context[0].parsed.y,
								quoteCurrency,
								context[0].parsed.y > 999999,
								'currency',
							);
						},
						label: (context) => {
							return new Date(context.label).toLocaleString('en', { dateStyle: 'medium' }).toUpperCase();
						},
					},
				},
			},
			hover: {
				mode: 'nearest',
				intersect: false,
			},
		},
	};
	return config;
}

export function roundUnixTime(unixTimestamp: number) {
	const date = new Date(unixTimestamp * 1000);
	// Round seconds down to the nearest minute
	date.setSeconds(0);
	// return the rounded Unix timestamp in seconds
	return date.getTime() / 1000;
}
